import React, { useEffect, useState } from 'react';
import { useApplicationStore } from '../../stores/applicationStore';
import { useAuthStore } from '../../stores/authStore';
import { 
  Download, 
  Trash2, 
  FileText, 
  Building2, 
  Calendar,
  Search,
  Filter,
  Eye,
  X,
  ExternalLink
} from 'lucide-react';
import { ref, deleteObject } from 'firebase/storage';
import { storage } from '../../services/firebase.config';
import { useNotificationStore } from '../../stores/notificationStore';

interface DocumentGroup {
  applicationId: string;
  companyName: string;
  position: string;
  appliedDate: Date;
  documents: Array<{
    name: string;
    url: string;
    type: string;
    size: number;
  }>;
}

export const DocumentsPage: React.FC = () => {
  const { applications, loadApplications, initialized, loading } = useApplicationStore();
  const { user } = useAuthStore();
  const addNotification = useNotificationStore(s => s.addNotification);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [deletingDoc, setDeletingDoc] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<{url: string, name: string, type: string} | null>(null);

  useEffect(() => {
    if (user?.uid && !initialized && !loading) {
      loadApplications(user.uid);
    }
  }, [user?.uid, initialized, loading, loadApplications]);

  // Raggruppa i documenti per candidatura
  const documentGroups: DocumentGroup[] = applications
    .filter(app => app.attachments && app.attachments.length > 0)
    .map(app => ({
      applicationId: app.id,
      companyName: app.companyName,
      position: app.position,
      appliedDate: app.appliedDate.toDate(),
      documents: app.attachments.map(att => ({
        name: att.name,
        url: att.url,
        type: att.type,
        size: att.size || 0
      }))
    }))
    .filter(group => {
      const matchesSearch = group.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           group.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           group.documents.some(doc => doc.name.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesFilter = filterType === 'all' || 
                           group.documents.some(doc => {
                             if (filterType === 'pdf') return doc.type === 'application/pdf';
                             if (filterType === 'doc') return doc.type.includes('document');
                             if (filterType === 'txt') return doc.type === 'text/plain';
                             return true;
                           });
      
      return matchesSearch && matchesFilter;
    });

  const totalDocuments = documentGroups.reduce((total, group) => total + group.documents.length, 0);

  const handleDownload = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    addNotification({
      type: 'info',
      title: 'Download avviato',
      message: `Il file "${fileName}" sta per essere scaricato.`
    });
  };

  const handlePreview = (document: {url: string, name: string, type: string}) => {
    setPreviewDoc(document);
  };

  const handleDeleteDocument = async (applicationId: string, documentIndex: number, documentUrl: string) => {
    if (!user?.uid) return;
    
    setDeletingDoc(documentUrl);
    try {
      // Elimina da Firebase Storage se è un file caricato
      if (documentUrl.startsWith('https://firebasestorage.googleapis.com')) {
        const storageRef = ref(storage, documentUrl);
        await deleteObject(storageRef);
      }
      
      // Aggiorna l'applicazione rimuovendo il documento
      const application = applications.find(app => app.id === applicationId);
      if (application) {
        const updatedAttachments = application.attachments.filter((_, index) => index !== documentIndex);
        // Qui dovresti chiamare updateApplicationInFirestore per aggiornare l'applicazione
        // Per ora ricarico tutte le applicazioni
        await loadApplications(user.uid);
      }
      
      addNotification({
        type: 'info',
        title: 'Documento eliminato',
        message: 'Il documento è stato eliminato con successo.'
      });
    } catch (error) {
      console.error('Errore nell\'eliminazione del documento:', error);
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Errore durante l\'eliminazione del documento.'
      });
    } finally {
      setDeletingDoc(null);
    }
  };

  const getFileIcon = (type: string) => {
    if (type === 'application/pdf') return '📄';
    if (type.includes('document')) return '📝';
    if (type === 'text/plain') return '📄';
    return '📎';
  };

  const canPreview = (type: string) => {
    return type === 'application/pdf' || type.includes('text/') || type.includes('image/');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (loading && !initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Caricamento documenti...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full flex justify-center px-2 sm:px-4 py-6">
        <div className="w-full max-w-4xl">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">I Miei Documenti</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">
                {totalDocuments} documento{totalDocuments !== 1 ? 'i' : ''} totali
              </p>
            </div>
          </div>

          {/* Filtri e Ricerca */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Ricerca */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Cerca documenti o aziende..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>

              {/* Filtro Tipo */}
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="all">Tutti i tipi</option>
                <option value="pdf">PDF</option>
                <option value="doc">Documenti Word</option>
                <option value="txt">File di testo</option>
              </select>

              {/* Reset Filtri */}
              {(searchTerm || filterType !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterType('all');
                  }}
                  className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors text-sm"
                >
                  Reset filtri
                </button>
              )}
            </div>
          </div>

          {/* Lista Documenti */}
          {documentGroups.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-10 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {searchTerm || filterType !== 'all' ? 'Nessun documento trovato' : 'Nessun documento'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {searchTerm || filterType !== 'all' 
                  ? 'Prova a modificare i filtri di ricerca'
                  : 'I tuoi documenti allegati alle candidature appariranno qui'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {documentGroups.map((group) => (
                <div key={group.applicationId} className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {/* Header Candidatura */}
                  <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                          <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {group.companyName}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {group.position}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center mt-1">
                            <Calendar className="h-3 w-3 mr-1" />
                            {group.appliedDate.toLocaleDateString('it-IT')}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {group.documents.length} documento{group.documents.length !== 1 ? 'i' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Lista Documenti */}
                  <div className="p-4 sm:p-6">
                    <div className="space-y-3">
                      {group.documents.map((document, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <div className="text-2xl">
                              {getFileIcon(document.type)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {document.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formatFileSize(document.size)} • {document.type}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            {/* Anteprima */}
                            {canPreview(document.type) && (
                              <button
                                onClick={() => handlePreview(document)}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                title="Anteprima"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            )}
                            
                            {/* Download */}
                            <button
                              onClick={() => handleDownload(document.url, document.name)}
                              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                              title="Scarica"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            
                            {/* Apri in nuova tab */}
                            <button
                              onClick={() => window.open(document.url, '_blank')}
                              className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                              title="Apri in nuova tab"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </button>
                            
                            {/* Elimina */}
                            <button
                              onClick={() => handleDeleteDocument(group.applicationId, index, document.url)}
                              disabled={deletingDoc === document.url}
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                              title="Elimina"
                            >
                              {deletingDoc === document.url ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal Anteprima */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header Modal */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="text-2xl">
                  {getFileIcon(previewDoc.type)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {previewDoc.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Anteprima documento
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleDownload(previewDoc.url, previewDoc.name)}
                  className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  title="Scarica"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setPreviewDoc(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Chiudi"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {/* Contenuto Modal */}
            <div className="flex-1 overflow-hidden">
              {previewDoc.type === 'application/pdf' ? (
                <iframe
                  src={`${previewDoc.url}#toolbar=0&navpanes=0&scrollbar=0`}
                  className="w-full h-full border-0"
                  title={previewDoc.name}
                />
              ) : previewDoc.type.includes('image/') ? (
                <div className="flex items-center justify-center h-full p-4">
                  <img
                    src={previewDoc.url}
                    alt={previewDoc.name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full p-4">
                  <div className="text-center">
                    <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Anteprima non disponibile per questo tipo di file
                    </p>
                    <button
                      onClick={() => window.open(previewDoc.url, '_blank')}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Apri in nuova tab
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}; 